import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ClipboardCheck, Loader2 } from 'lucide-react';
import { CampaignPausedBanner } from '../../components/CampaignPausedBanner';
import { Logo } from '../../components/Logo';
import { UserMenu } from '../../components/UserMenu';
import { useAuth } from '../../contexts/AuthContext';
import { useTaskTypeInstructions } from '../../hooks/useTaskTypeInstructions';
import { useIsPhone } from '../../hooks/useIsMobile';
import { useViewModelReady } from '../../hooks/useViewModel';
import { TaskShell } from '../../components/TaskShell';
import { ConfirmDialog } from '../../components/TaskShell/ConfirmDialog';
import { MobileTaskUnavailableView } from './MobileTaskUnavailableView';
import { EscalateReviewDialog } from './EscalateReviewDialog';
import { useTaskDetailViewModel } from './viewModel';
import { paths } from '../../routes/paths';
import {
  findUnassignedReviewBrowsePosition,
  readUnassignedReviewBrowseState,
} from '../../domain/taskQueue/unassignedReviewBrowse';
import { UnassignedReviewBrowseControls } from './UnassignedReviewBrowseControls';

const APP_HEADER_HEIGHT_PX = 64;
const REVIEW_BROWSE_BAR_HEIGHT_PX = 48;

export function TaskDetailPage() {
  const isPhone = useIsPhone();
  return <DesktopTaskDetailPage isPhone={isPhone} />;
}

function DesktopTaskDetailPage({ isPhone }: { isPhone: boolean }) {
  const { taskId } = useParams<{ taskId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin, currentUser } = useAuth();
  const vm = useTaskDetailViewModel(taskId);
  const instructionsState = useTaskTypeInstructions(vm.task?.taskType ?? null);
  const [reworkOpen, setReworkOpen] = useState(false);
  const [returnToQueueOpen, setReturnToQueueOpen] = useState(false);
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [verificationPanelVisible, setVerificationPanelVisible] = useState(false);
  const reviewBrowseState = readUnassignedReviewBrowseState(location.state);
  const reviewBrowsePosition = reviewBrowseState && taskId
    ? findUnassignedReviewBrowsePosition(reviewBrowseState, taskId)
    : null;
  const activeReviewBrowsePosition = reviewBrowsePosition
    && (!vm.task || vm.task.campaignId === reviewBrowseState?.campaignId)
    ? reviewBrowsePosition
    : null;
  useViewModelReady(vm.initialize);

  // Fallback: if an admin archives this task (or any other non-self action
  // moves it to a terminal state while the user is viewing), bounce back to
  // the queue. Self-initiated submit/approve/requestChanges are handled by
  // the action wrappers below.
  useEffect(() => {
    if (!vm.task) return;
    if (vm.task.status !== 'archived') return;
    if (vm.pendingAction !== null) return;
    const id = setTimeout(() => navigate(paths.queue()), 1200);
    return () => clearTimeout(id);
  }, [vm.task?.status, vm.pendingAction, navigate, vm.task]);

  // Wide task types already use the full viewport. Review mode and an open
  // verification rail also expand both header rows so their controls are not
  // constrained by the task body's standard width.
  const bleed = vm.module?.widthMode === 'wide';
  const fullWidthHeaders = bleed || vm.mode === 'review' || verificationPanelVisible;

  const navigateToQueueAfter = (ok: boolean) => {
    if (ok) setTimeout(() => navigate(paths.queue()), 600);
  };

  const handleSubmit = async () => {
    navigateToQueueAfter(await vm.submit());
  };
  const handleAbandon = async () => {
    navigateToQueueAfter(await vm.abandon());
  };
  const handleApprove = async () => {
    navigateToQueueAfter(await vm.approve());
  };
  // The reviewer's Request Changes action no longer collects a modal
  // comment — the gate in the shell already requires at least one unresolved
  // field comment to exist, so the feedback is captured by the field comments
  // themselves. Pass an empty string so the old `requestChangesAction`
  // signature keeps working for any internal callers that still need it.
  const handleRequestChanges = async (_comment: string) => {
    navigateToQueueAfter(await vm.requestChangesAction(''));
  };
  const handleRework = async () => {
    setReworkOpen(true);
  };
  const confirmRework = async () => {
    setReworkOpen(false);
    navigateToQueueAfter(await vm.rework());
  };
  const handleReturnToQueue = async () => {
    setReturnToQueueOpen(true);
  };
  const confirmReturnToQueue = async () => {
    setReturnToQueueOpen(false);
    navigateToQueueAfter(await vm.returnToQueue());
  };
  const handleReleaseReview = async () => {
    navigateToQueueAfter(await vm.releaseFromReview());
  };
  const confirmEscalation = async (note: string) => {
    const ok = await vm.escalate(note);
    if (!ok) return;
    setEscalateOpen(false);
    navigateToQueueAfter(true);
  };
  const navigateWithinReviewBrowse = (nextTaskId: string | null) => {
    if (!nextTaskId || !reviewBrowseState) return;
    navigate(paths.task(nextTaskId), { state: reviewBrowseState });
  };
  const handleBrowseClaim = async () => {
    if (!taskId) return;
    const claimed = await vm.claimForReview();
    if (claimed) {
      navigate(paths.task(taskId), { replace: true, state: null });
    }
  };

  if (vm.campaignPaused) {
    return (
      <main className="min-h-screen bg-gray-50 px-4 py-8">
        <div className="mx-auto max-w-6xl space-y-6">
          <button type="button" onClick={() => navigate(paths.queue())} className="text-sm text-gray-600 hover:text-gray-900">
            Back to queue
          </button>
          <CampaignPausedBanner />
        </div>
      </main>
    );
  }
  if (isPhone) return <MobileTaskUnavailableView />;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* App-level header is sticky so the user always has the logo + back
          button + account menu in reach. */}
      <header
        aria-label="Application header"
        className="sticky top-0 z-30 h-16 bg-white border-b border-gray-200"
      >
        <div
          role="group"
          aria-label="Application header content"
          className={
            fullWidthHeaders
              ? 'px-4 sm:px-6 lg:px-8 h-full flex items-center justify-between'
              : 'max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center justify-between'
          }
        >
          <div className="flex items-center gap-2">
            <ClipboardCheck className="h-7 w-7 text-primary" />
            <Logo className="text-xl font-bold text-gray-900" />
          </div>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => navigate(paths.queue())}
              className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to queue
            </button>
            <UserMenu />
          </div>
        </div>
      </header>

      {activeReviewBrowsePosition && (
        <UnassignedReviewBrowseControls
          index={activeReviewBrowsePosition.index}
          total={activeReviewBrowsePosition.total}
          bleed={fullWidthHeaders}
          hasPrevious={activeReviewBrowsePosition.previousTaskId !== null}
          hasNext={activeReviewBrowsePosition.nextTaskId !== null}
          canClaim={vm.canClaimReview}
          claiming={vm.pendingAction === 'claimReview'}
          onPrevious={() => navigateWithinReviewBrowse(activeReviewBrowsePosition.previousTaskId)}
          onNext={() => navigateWithinReviewBrowse(activeReviewBrowsePosition.nextTaskId)}
          onClaim={handleBrowseClaim}
        />
      )}

      {vm.busy ? (
        <div className="flex items-center justify-center py-24 text-gray-500">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          Loading task…
        </div>
      ) : vm.error ? (
        <div className="mx-auto mt-10 max-w-md rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {vm.error}
        </div>
      ) : vm.runtimeStatus === 'loading' ? (
        <div className="flex items-center justify-center py-24 text-gray-500">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          Loading task runtime…
        </div>
      ) : vm.runtimeStatus === 'error' || !vm.module ? (
        <div className="mx-auto mt-10 max-w-md rounded-md border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-800">
          <p className="font-medium">
            {vm.runtimeStaleDeployment
              ? 'This task runtime is from an older deployment.'
              : 'The task runtime could not be loaded.'}
          </p>
          {vm.runtimeError && <p className="mt-1 text-xs text-red-700">{vm.runtimeError.message}</p>}
          <button
            type="button"
            onClick={vm.retryRuntime}
            className="mt-3 rounded-md border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50"
          >
            Try again
          </button>
        </div>
      ) : (
        <TaskShell
          mode={vm.mode}
          module={vm.module}
          task={vm.task}
          result={vm.draftResult}
          onResultChange={vm.setDraftResult}
          pendingAction={vm.pendingAction}
          pendingCommentId={vm.pendingCommentId}
          error={vm.actionError}
          canClaimReview={vm.canClaimReview}
          canEditResult={vm.canEditResult}
          onBeforeReload={vm.flushForReload}
          canRename={vm.canRename}
          fullBleedHeader={fullWidthHeaders}
          onVerificationPanelVisibilityChange={setVerificationPanelVisible}
          currentUserUid={currentUser?.uid ?? null}
          comments={vm.comments}
          isAdminViewer={isAdmin}
          instructions={instructionsState.instructions}
          validatorEnabled={vm.validatorEnabled}
          validatorCommentsByField={vm.validatorCommentsByField}
          dismissedValidatorIds={vm.dismissedValidatorIds}
          validatorStatus={vm.validatorStatus}
          openPanelRequest={{ panelId: 'verification', token: vm.verificationFocusToken }}
          validatorError={vm.validatorError}
          stickyTopPx={APP_HEADER_HEIGHT_PX + (
            activeReviewBrowsePosition ? REVIEW_BROWSE_BAR_HEIGHT_PX : 0
          )}
          actions={{
            onSaveDraft: vm.saveDraft,
            onSubmit: handleSubmit,
            onAbandon: handleAbandon,
            onApprove: handleApprove,
            onRework: vm.canRework ? handleRework : undefined,
            onReturnToQueue: vm.canReturnToQueue ? handleReturnToQueue : undefined,
            onRequestChanges: handleRequestChanges,
            onEscalate: vm.canEscalateReview
              ? async () => setEscalateOpen(true)
              : undefined,
            onClaimReview: activeReviewBrowsePosition
              ? handleBrowseClaim
              : async () => {
                  await vm.claimForReview();
                },
            onReleaseReview: vm.canReleaseReview ? handleReleaseReview : undefined,
            onAddFieldComment: vm.addFieldComment,
            onAddReply: vm.addReply,
            onEditComment: vm.editComment,
            onDeleteComment: vm.deleteComment,
            onMarkAddressed: vm.markAddressed,
            onRenameTask: vm.setTitle,
            onApproveLlmComment: vm.approveLlmComment,
            onEditLlmComment: vm.editLlmComment,
            onDeleteLlmComment: vm.deleteLlmComment,
            onRunValidation: vm.runValidation,
            onDismissValidatorComment: vm.dismissValidatorComment,
            onRestoreValidatorComment: vm.restoreValidatorComment,
          }}
        />
      )}
      <ConfirmDialog
        open={reworkOpen}
        title="Reopen this approved task?"
        message={
          'This pulls the task out of the approved pile and back into your '
          + 'rework queue.\n\nThe existing answer is preserved as your starting '
          + 'draft. A reviewer will pick it up again after you re-submit.'
        }
        confirmLabel="Reopen for rework"
        busy={vm.pendingAction === 'rework'}
        onCancel={() => setReworkOpen(false)}
        onConfirm={confirmRework}
      />
      <ConfirmDialog
        open={returnToQueueOpen}
        title="Move this submission back to your queue?"
        message={
          'This removes the task from the awaiting-review pool and puts it '
          + 'back in My Tasks so you can edit the submitted answer.\n\n'
          + 'You will need to submit it again when your changes are ready.'
        }
        confirmLabel="Move back to queue"
        busy={vm.pendingAction === 'returnToQueue'}
        onCancel={() => setReturnToQueueOpen(false)}
        onConfirm={confirmReturnToQueue}
      />
      <EscalateReviewDialog
        open={escalateOpen}
        busy={vm.pendingAction === 'escalate'}
        error={vm.actionError}
        onCancel={() => setEscalateOpen(false)}
        onConfirm={confirmEscalation}
      />
    </div>
  );
}
